import { useState, useEffect, useCallback } from "react";
import {
  HiOutlineQueueList, HiOutlinePaperAirplane, HiOutlineXMark,
  HiOutlineClock, HiOutlineShieldCheck, HiOutlineEnvelope,
  HiOutlineArrowUturnLeft, HiOutlineNoSymbol, HiOutlineCalendarDays,
  HiOutlineSparkles, HiOutlineHome, HiOutlineBellAlert, HiOutlineUserPlus,
} from "react-icons/hi2";
import AdminLayout from "./AdminLayout";
import { queueAPI, retreatBlocksAPI } from "../services/api";
import { useCurrentAdmin, hasPermission } from "./permissions";
import toast from "react-hot-toast";
import "./AdminQueue.css";

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-US", { year:"numeric", month:"short", day:"numeric" }) : "—";
const fmtDT = (d) => d ? new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";
const fmtMoney = (n) => `$${Number(n||0).toLocaleString("en-US",{minimumFractionDigits:0})}`;

// Default deadline suggestion — today + 7 days, as a yyyy-mm-dd string for <input type="date">
const defaultDeadline = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
};

/* ── Send targeted offer modal — one applicant, one block, one room type, custom deadline ── */
function OfferModal({ applicant, blocks, onClose, onSent }) {
  const [selectedBlock, setSelectedBlock] = useState("");
  const [selectedRoomType, setSelectedRoomType] = useState("");
  const [deadline, setDeadline] = useState(defaultDeadline());
  const [sending, setSending] = useState(false);
  const [error,   setError]   = useState("");

  const eligibleBlocks = blocks.filter(b => ["upcoming","open"].includes(b.status));
  const chosenBlock = eligibleBlocks.find(b => String(b.id) === String(selectedBlock));
  const availableRoomTypes = chosenBlock
    ? chosenBlock.room_types.filter(rt => (rt.assigned_count + rt.offered_count) < rt.capacity)
    : [];

  const handleSend = async () => {
    if (!selectedBlock) { setError("Please select a retreat block."); return; }
    if (availableRoomTypes.length > 0 && !selectedRoomType) { setError("Please select a room type."); return; }
    if (!deadline) { setError("Please set a response deadline."); return; }
    setSending(true); setError("");
    try {
      await queueAPI.sendOffer(applicant.id, selectedBlock, selectedRoomType || null, `${deadline}T23:59:59`);
      toast.success(`Offer sent to ${applicant.first_name}.`);
      onSent();
    } catch (err) {
      setError(err.message || "Failed to send offer.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="aap__contact-overlay" onClick={onClose}>
      <div className="aap__contact-modal" onClick={e=>e.stopPropagation()} style={{maxWidth:"440px"}}>
        <div className="aap__contact-head">
          <h3>Send Retreat Offer</h3>
          <button type="button" onClick={onClose}><HiOutlineXMark/></button>
        </div>
        <p className="aap__contact-name">{applicant.first_name} {applicant.last_name}</p>
        <p style={{fontSize:"0.8rem",color:"#6b6258",margin:"0.25rem 0 1rem"}}>{applicant.appRef} · Waiting {applicant.waitDays} day{applicant.waitDays!==1?"s":""}</p>

        {eligibleBlocks.length === 0 ? (
          <p style={{fontSize:"0.85rem",color:"#9ca3af",textAlign:"center",padding:"1.5rem 0"}}>
            No retreat blocks available. Create one first.
          </p>
        ) : (
          <>
            <div style={{display:"flex",flexDirection:"column",gap:"0.3rem",marginBottom:"0.85rem"}}>
              <label style={{fontSize:"0.78rem",fontWeight:700,color:"#1b1b1b"}}>Select Retreat Block</label>
              <select className="arb__form-input arb__form-select" value={selectedBlock}
                onChange={e=>{setSelectedBlock(e.target.value);setSelectedRoomType("");setError("");}}>
                <option value="">Choose a block…</option>
                {eligibleBlocks.map(b => (
                  <option key={b.id} value={b.id}>{b.name} — {fmt(b.start_date)}</option>
                ))}
              </select>
            </div>

            {chosenBlock && (
              <div style={{display:"flex",flexDirection:"column",gap:"0.3rem",marginBottom:"0.85rem"}}>
                <label style={{fontSize:"0.78rem",fontWeight:700,color:"#1b1b1b"}}>Select Room Type</label>
                {availableRoomTypes.length === 0 ? (
                  <p style={{fontSize:"0.8rem",color:"#dc2626"}}>No room types have availability in this block.</p>
                ) : (
                  <select className="arb__form-input arb__form-select" value={selectedRoomType}
                    onChange={e=>{setSelectedRoomType(e.target.value);setError("");}}>
                    <option value="">Choose a room type…</option>
                    {availableRoomTypes.map(rt => (
                      <option key={rt.room_type_id} value={rt.room_type_id}>
                        {rt.name} — {rt.is_free?"Free":fmtMoney(rt.rate)} ({rt.capacity - rt.assigned_count - rt.offered_count} left)
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            <div style={{display:"flex",flexDirection:"column",gap:"0.3rem",marginBottom:"1rem"}}>
              <label style={{fontSize:"0.78rem",fontWeight:700,color:"#1b1b1b"}}>Respond By (deadline)</label>
              <input type="date" className="arb__form-input" value={deadline}
                min={new Date().toISOString().slice(0,10)}
                onChange={e=>{setDeadline(e.target.value);setError("");}}/>
            </div>

            {error && <p style={{fontSize:"0.8rem",fontWeight:600,color:"#dc2626",marginBottom:"0.75rem"}}>{error}</p>}
            <button type="button" className="a-btn a-btn--rust" style={{width:"100%"}} disabled={sending} onClick={handleSend}>
              <HiOutlinePaperAirplane/> {sending ? "Sending…" : "Send Offer"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Batch activation modal — custom deadline ── */
function ActivateModal({ count, onClose, onConfirm, sending }) {
  const [deadline, setDeadline] = useState(defaultDeadline());

  return (
    <div className="aap__contact-overlay" onClick={onClose}>
      <div className="aap__contact-modal" onClick={e=>e.stopPropagation()} style={{maxWidth:"420px"}}>
        <div className="aap__contact-head">
          <h3>Activate for Open Selection</h3>
          <button type="button" onClick={onClose}><HiOutlineXMark/></button>
        </div>
        <p style={{fontSize:"0.88rem",color:"#1b1b1b",lineHeight:1.6,marginBottom:"1rem"}}>
          <strong>{count} applicant{count!==1?"s":""}</strong> will be emailed and invited to log into
          their portal to choose their own dates from any retreat block currently marked <strong>"Open"</strong>.
        </p>
        <p style={{fontSize:"0.8rem",color:"#6b6258",marginBottom:"1rem"}}>
          Spots are first-come, first-served among everyone activated. Make sure the blocks you want
          them to choose from are set to "Open" status first.
        </p>
        <div style={{display:"flex",flexDirection:"column",gap:"0.3rem",marginBottom:"1.25rem"}}>
          <label style={{fontSize:"0.78rem",fontWeight:700,color:"#1b1b1b"}}>Respond By (deadline)</label>
          <input type="date" className="arb__form-input" value={deadline}
            min={new Date().toISOString().slice(0,10)}
            onChange={e=>setDeadline(e.target.value)}/>
        </div>
        <div style={{display:"flex",gap:"0.5rem",justifyContent:"flex-end"}}>
          <button type="button" className="a-btn a-btn--outline" onClick={onClose}>Cancel</button>
          <button type="button" className="a-btn a-btn--rust" disabled={sending} onClick={()=>onConfirm(`${deadline}T23:59:59`)}>
            <HiOutlineSparkles/> {sending ? "Activating…" : `Activate ${count} Applicant${count!==1?"s":""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminQueue() {
  const { admin, loading: permLoading } = useCurrentAdmin();
  const canManage = permLoading ? true : hasPermission(admin, "queue.manage");

  const [queue,     setQueue]     = useState([]);
  const [eligible,  setEligible]  = useState([]);
  const [blocks,    setBlocks]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [offering,  setOffering]  = useState(null); // applicant object
  const [busyId,    setBusyId]    = useState(null);
  const [selected,  setSelected]  = useState(new Set());
  const [showActivate, setShowActivate] = useState(false);
  const [activating, setActivating] = useState(false);
  const [selectN,   setSelectN]   = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // scope:"active" matches the upcoming/open filter applied below — now
      // done server-side so this stays fast as archived retreats accumulate.
      const [q, b, e] = await Promise.all([
        queueAPI.list(),
        retreatBlocksAPI.list({ scope: "active" }),
        queueAPI.eligible(),
      ]);
      setQueue(q?.data || []);
      setBlocks(b?.data || []);
      setEligible(e?.data || []);
    } catch (err) {
      toast.error("Failed to load queue.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleWithdraw = async (id) => {
    setBusyId(id);
    try { await queueAPI.withdrawOffer(id); toast.success("Offer withdrawn."); fetchAll(); }
    catch (err) { toast.error(err.message || "Failed."); }
    finally { setBusyId(null); }
  };

  const handleRemind = async (id) => {
    setBusyId(id);
    try { await queueAPI.sendReminder(id); toast.success("Reminder sent."); }
    catch (err) { toast.error(err.message || "Failed."); }
    finally { setBusyId(null); }
  };

  const handleAddToQueue = async (applicant) => {
    setBusyId(applicant.id);
    try { await queueAPI.requeue(applicant.id); toast.success(`${applicant.first_name} added to the queue.`); fetchAll(); }
    catch (err) { toast.error(err.message || "Failed."); }
    finally { setBusyId(null); }
  };

  const handleRemove = (applicant) => {
    toast((t) => (
      <div style={{display:"flex",flexDirection:"column",gap:"0.5rem"}}>
        <p style={{margin:0,fontWeight:700}}>Remove {applicant.first_name} from the queue?</p>
        <p style={{margin:0,fontSize:"0.78rem",color:"#6b6258"}}>They can be added back later.</p>
        <div style={{display:"flex",gap:"0.5rem"}}>
          <button onClick={async ()=>{
            toast.dismiss(t.id); setBusyId(applicant.id);
            try { await queueAPI.remove(applicant.id); toast.success("Removed from queue."); fetchAll(); }
            catch(err){ toast.error(err.message||"Failed."); }
            finally { setBusyId(null); }
          }} style={{background:"#dc2626",color:"#fff",border:"none",borderRadius:"5px",padding:"0.35rem 0.75rem",cursor:"pointer",fontWeight:700,fontSize:"0.78rem"}}>Remove</button>
          <button onClick={()=>toast.dismiss(t.id)} style={{background:"#f3f4f0",border:"none",borderRadius:"5px",padding:"0.35rem 0.75rem",cursor:"pointer",fontSize:"0.78rem"}}>Cancel</button>
        </div>
      </div>
    ), { duration:Infinity, style:{maxWidth:"320px"} });
  };

  const handleActivateBatch = async (expiresAt) => {
    setActivating(true);
    try {
      const res = await queueAPI.activateBatch([...selected], expiresAt);
      toast.success(res?.message || "Applicants activated.");
      setSelected(new Set());
      setShowActivate(false);
      fetchAll();
    } catch (err) {
      toast.error(err.message || "Failed to activate.");
    } finally {
      setActivating(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSelectFirstN = () => {
    const n = parseInt(selectN);
    if (!n || n <= 0) { toast.error("Enter a number greater than 0."); return; }
    const firstN = queued.slice(0, n).map(a => a.id);
    setSelected(new Set(firstN));
  };

  const queued  = queue.filter(a => a.queue_status === "queued");
  const offered = queue.filter(a => a.queue_status === "offered");
  const openBlocksCount = blocks.filter(b => b.status === "open").length;

  return (
    <AdminLayout pageTitle="Retreat Queue" pageSubtitle="Manage applicants waiting for a retreat spot." permission="queue.view">

      {/* Summary stats */}
      <div className="aap__stats-row" style={{gridTemplateColumns:"repeat(3, 1fr)"}}>
        <div className="acard aap__stat-card">
          <div className="aap__stat-top">
            <div className="aap__stat-icon" style={{background:"rgba(217,119,6,0.1)"}}><HiOutlineQueueList style={{color:"#d97706",fontSize:"1.1rem"}}/></div>
            <p className="aap__stat-label">Waiting in Queue</p>
          </div>
          <p className="aap__stat-value">{queued.length}</p>
        </div>
        <div className="acard aap__stat-card">
          <div className="aap__stat-top">
            <div className="aap__stat-icon" style={{background:"rgba(37,99,235,0.1)"}}><HiOutlineClock style={{color:"#2563eb",fontSize:"1.1rem"}}/></div>
            <p className="aap__stat-label">Pending Offers</p>
          </div>
          <p className="aap__stat-value">{offered.length}</p>
        </div>
        <div className="acard aap__stat-card">
          <div className="aap__stat-top">
            <div className="aap__stat-icon" style={{background:"rgba(107,98,88,0.1)"}}><HiOutlineUserPlus style={{color:"#6b6258",fontSize:"1.1rem"}}/></div>
            <p className="aap__stat-label">Eligible, Not Queued</p>
          </div>
          <p className="aap__stat-value">{eligible.length}</p>
        </div>
      </div>

      {/* Eligible for queue — the "approve someone" entry point */}
      {eligible.length > 0 && (
        <div className="acard aq__section">
          <p className="p-section-title" style={{padding:"1.1rem 1.25rem 0.5rem"}}>Eligible for the Queue</p>
          <p style={{fontSize:"0.8rem",color:"#6b6258",padding:"0 1.25rem 0.75rem"}}>
            These applicants are fully medically cleared but not currently in the retreat queue.
            Add them to the queue below to start offering them dates.
          </p>
          {eligible.map(a => (
            <div key={a.id} className="aq__row">
              <div className="aq__row-info">
                <p className="aq__row-name">
                  {a.first_name} {a.last_name}
                  {a.isAlumni && <span className="aq__alumni-badge"><HiOutlineSparkles/> Alumni</span>}
                </p>
                <p className="aq__row-meta">{a.appRef} · <HiOutlineShieldCheck style={{verticalAlign:"-2px"}}/> {a.category}</p>
                <p className="aq__row-wait">
                  {a.isAlumni
                    ? `Attended ${a.last_block_name || "a retreat"}${a.last_block_end_date ? ` — ${fmt(a.last_block_end_date)}` : ""}`
                    : `Cleared ${fmt(a.medical_cleared_at)}${a.queue_status==="declined" ? " · Previously removed from queue" : ""}`}
                </p>
              </div>
              {canManage && (
                <button type="button" className="a-btn a-btn--rust" disabled={busyId===a.id}
                  onClick={()=>handleAddToQueue(a)}>
                  <HiOutlineUserPlus/> {busyId===a.id?"…":"Add to Queue"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="arb__loading"><div className="acr__spinner"/></div>
      ) : queue.length === 0 && eligible.length === 0 ? (
        <div className="acard arb__empty">
          <HiOutlineQueueList className="arb__empty-icon"/>
          <p className="arb__empty-title">Queue is empty</p>
          <p className="arb__empty-sub">Applicants automatically join the queue once their medical clearance is approved.</p>
        </div>
      ) : (
        <>
          {/* Pending offers */}
          {offered.length > 0 && (
            <div className="acard aq__section">
              <p className="p-section-title" style={{padding:"1.1rem 1.25rem 0.5rem"}}>Pending Offers</p>
              {offered.map(a => (
                <div key={a.id} className="aq__row">
                  <div className="aq__row-info">
                    <p className="aq__row-name">{a.first_name} {a.last_name}</p>
                    <p className="aq__row-meta">{a.appRef} · <HiOutlineShieldCheck style={{verticalAlign:"-2px"}}/> {a.category}</p>
                    {a.openSelection ? (
                      <p className="aq__row-offer">
                        <HiOutlineSparkles/> Activated — choosing from open dates · expires {fmtDT(a.offer_expires_at)}
                      </p>
                    ) : (
                      <p className="aq__row-offer">
                        <HiOutlineCalendarDays/> Offered <strong>{a.block_name}</strong> ({fmt(a.block_start_date)})
                        {a.offered_room_type_name ? ` · ${a.offered_room_type_name}` : ""} · expires {fmtDT(a.offer_expires_at)}
                      </p>
                    )}
                  </div>
                  {canManage && (
                    <div style={{display:"flex",gap:"0.5rem"}}>
                      <button type="button" className="a-btn a-btn--outline" disabled={busyId===a.id}
                        onClick={()=>handleRemind(a.id)} title="Send reminder email">
                        <HiOutlineBellAlert/> {busyId===a.id?"…":"Remind"}
                      </button>
                      <button type="button" className="a-btn a-btn--outline" disabled={busyId===a.id}
                        onClick={()=>handleWithdraw(a.id)}>
                        <HiOutlineArrowUturnLeft/> {busyId===a.id?"…":"Withdraw"}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Queue — FIFO with batch selection */}
          <div className="acard aq__section">
            <div className="aq__section-head">
              <p className="p-section-title" style={{margin:0}}>Waiting Queue (FIFO)</p>
              {canManage && queued.length > 0 && (
                <div className="aq__batch-controls">
                  <span style={{fontSize:"0.78rem",color:"#6b6258"}}>Select first</span>
                  <input type="number" min="1" max={queued.length} className="aq__select-n-input"
                    value={selectN} placeholder="N"
                    onChange={e=>setSelectN(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleSelectFirstN()}/>
                  <button type="button" className="a-btn a-btn--ghost" onClick={handleSelectFirstN}>Select</button>
                  <button type="button" className="a-btn a-btn--ghost" onClick={()=>{setSelected(new Set());setSelectN("");}} disabled={selected.size===0}>Clear</button>
                  <button type="button" className="a-btn a-btn--rust" disabled={selected.size===0}
                    onClick={()=>setShowActivate(true)}>
                    <HiOutlineSparkles/> Activate Selected ({selected.size})
                  </button>
                </div>
              )}
            </div>
            {openBlocksCount === 0 && canManage && (
              <p className="aq__open-warning">
                <HiOutlineHome/> No retreat blocks are currently marked "Open" — batch activation needs at least one open block to offer.
              </p>
            )}
            {queued.length === 0 ? (
              <p className="aap__placeholder" style={{padding:"1.5rem",textAlign:"center"}}>No one currently waiting.</p>
            ) : queued.map((a, i) => (
              <div key={a.id} className="aq__row">
                {canManage && (
                  <input type="checkbox" className="aq__checkbox" checked={selected.has(a.id)}
                    onChange={()=>toggleSelect(a.id)}/>
                )}
                <div className="aq__row-num">{i+1}</div>
                <div className="aq__row-info">
                  <p className="aq__row-name">{a.first_name} {a.last_name}</p>
                  <p className="aq__row-meta">{a.appRef} · <HiOutlineShieldCheck style={{verticalAlign:"-2px"}}/> {a.category}</p>
                  <p className="aq__row-wait"><HiOutlineClock/> Waiting {a.waitDays} day{a.waitDays!==1?"s":""} · <HiOutlineEnvelope style={{verticalAlign:"-2px"}}/> {a.email}</p>
                </div>
                {canManage && (
                  <div style={{display:"flex",gap:"0.5rem"}}>
                    <button type="button" className="a-btn a-btn--rust" onClick={()=>setOffering(a)}>
                      <HiOutlinePaperAirplane/> Send Offer
                    </button>
                    <button type="button" className="a-btn a-btn--ghost" style={{color:"#dc2626"}}
                      onClick={()=>handleRemove(a)} title="Remove from queue">
                      <HiOutlineNoSymbol/>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {offering && (
        <OfferModal applicant={offering} blocks={blocks}
          onClose={()=>setOffering(null)}
          onSent={()=>{ setOffering(null); fetchAll(); }}/>
      )}

      {showActivate && (
        <ActivateModal count={selected.size} sending={activating}
          onClose={()=>setShowActivate(false)}
          onConfirm={handleActivateBatch}/>
      )}
    </AdminLayout>
  );
}
